/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  getUserFullName,
  getUserPosition,
  getUserCompany,
} from '@e180/domain/lib/user/util';

import styles from './UserBanner.style';
import UserAvatar, {
  small as avatarSmall,
  medium as avatarMedium,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import messages from 'src/shared/domain/user/l10n/userL10n';

import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  checkedIn?: boolean,
  variant?: typeof small,
  onDarkBackground?: boolean,
  withIcon?: boolean,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const UserBanner = ({
  user,
  checkedIn,
  variant,
  onDarkBackground,
  withIcon,
  intl,
}: Props): React.Node => {
  const { locale } = intl;
  const name = getUserFullName(user);
  const position = getUserPosition(user, locale);
  const company = getUserCompany(user, locale);
  const title =
    position && company
      ? intl.formatMessage(messages.fullPosition, {
          position,
          company,
        })
      : position || company;

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, {
    [`${classes['root-darkBg']}`]: onDarkBackground,
  });

  const textClasses = classNames(classes.text, {
    [`${classes['text-withIcon']}`]: withIcon,
  });

  const nameClasses = classNames(classes.name, {
    [`${classes['name-checkedIn']}`]: checkedIn,
    [`${classes['name-notCheckedIn']}`]: checkedIn === false,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <div className={rootClasses}>
      <UserAvatar
        user={user}
        variant={variant === small ? avatarSmall : avatarMedium}
        showBadge
      />
      <div className={textClasses}>
        <span className={nameClasses}>{name}</span>
        <span className={classes.title}>{title}</span>
      </div>
    </div>
  );
};

export const small = Symbol();

export default compose(React.memo, injectIntl)(UserBanner);
